import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Clock, FileText, Edit3, CheckSquare, HelpCircle, X, ChevronRight } from 'react-feather';
import styles from '../styles/testSetup.module.css';

const TestSetup = ({ onStart, onCancel, defaultTopic = '' }) => {
  const [config, setConfig] = useState({
    topic: defaultTopic,
    questionCount: 10,
    testType: 'mixed', // mixed, multipleChoice, fillBlank, written, trueFalse
    difficulty: 'medium',
    timeLimit: 0, // 0 = no limit, else minutes
    showFeedback: true,
    allowSkip: true,
    randomOrder: true
  });

  const testTypes = [
    {
      id: 'mixed',
      label: 'Mixed',
      icon: <FileText size={20} />,
      description: 'Variety of question types'
    },
    {
      id: 'multipleChoice',
      label: 'Multiple Choice',
      icon: <CheckSquare size={20} />,
      description: 'Select the correct answer'
    },
    {
      id: 'fillBlank',
      label: 'Fill in the Blank',
      icon: <Edit3 size={20} />,
      description: 'Type the missing words'
    },
    {
      id: 'written',
      label: 'Written Response',
      icon: <FileText size={20} />,
      description: 'Free-form answers'
    },
    {
      id: 'trueFalse',
      label: 'True/False',
      icon: <HelpCircle size={20} />,
      description: 'Simple true or false'
    }
  ];

  const difficulties = [
    { id: 'easy', label: 'Easy', color: '#10b981' },
    { id: 'medium', label: 'Medium', color: '#3b82f6' },
    { id: 'hard', label: 'Hard', color: '#f59e0b' },
    { id: 'expert', label: 'Expert', color: '#ef4444' }
  ];

  const questionCounts = [5, 10, 15, 20, 25, 30, 50];
  const timeLimits = [
    { value: 0, label: 'No Limit' },
    { value: 10, label: '10 min' },
    { value: 20, label: '20 min' },
    { value: 30, label: '30 min' },
    { value: 45, label: '45 min' },
    { value: 60, label: '60 min' },
    { value: 90, label: '90 min' }
  ];

  const handleStart = () => {
    if (!config.topic.trim()) {
      alert('Please enter a topic for your test');
      return;
    }
    onStart(config);
  };

  return (
    <div className={styles.setupOverlay}>
      <motion.div 
        className={styles.setupContainer}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
      >
        {/* Header */}
        <div className={styles.setupHeader}>
          <div>
            <h2 className={styles.setupTitle}>Create Your Test</h2>
            <p className={styles.setupSubtitle}>Configure your test settings</p>
          </div>
          {onCancel && (
            <button className={styles.closeBtn} onClick={onCancel}>
              <X size={20} />
            </button>
          )}
        </div>

        {/* Content */}
        <div className={styles.setupContent}>
          {/* Topic */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              <FileText size={16} />
              <span>Test Topic</span>
            </label>
            <input
              type="text"
              className={styles.input}
              placeholder="e.g., World War II, Calculus, Python Programming"
              value={config.topic}
              onChange={(e) => setConfig({ ...config, topic: e.target.value })}
              autoFocus
            />
          </div>

          {/* Test Type */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Question Type</label>
            <div className={styles.typeGrid}>
              {testTypes.map((type) => (
                <button
                  key={type.id}
                  className={`${styles.typeCard} ${config.testType === type.id ? styles.typeCardActive : ''}`}
                  onClick={() => setConfig({ ...config, testType: type.id })}
                >
                  <div className={styles.typeIcon}>{type.icon}</div>
                  <div className={styles.typeContent}>
                    <div className={styles.typeLabel}>{type.label}</div>
                    <div className={styles.typeDesc}>{type.description}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Question Count and Difficulty */}
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Number of Questions</label>
              <div className={styles.buttonGroup}>
                {questionCounts.map((count) => (
                  <button
                    key={count}
                    className={`${styles.optionBtn} ${config.questionCount === count ? styles.optionBtnActive : ''}`}
                    onClick={() => setConfig({ ...config, questionCount: count })}
                  >
                    {count}
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Difficulty</label>
              <div className={styles.buttonGroup}>
                {difficulties.map((diff) => (
                  <button
                    key={diff.id}
                    className={`${styles.optionBtn} ${config.difficulty === diff.id ? styles.optionBtnActive : ''}`}
                    onClick={() => setConfig({ ...config, difficulty: diff.id })}
                    style={config.difficulty === diff.id ? { borderColor: diff.color, color: diff.color } : {}}
                  >
                    {diff.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Time Limit */}
          <div className={styles.formGroup}>
            <label className={styles.label}>
              <Clock size={16} />
              <span>Time Limit</span>
            </label>
            <div className={styles.buttonGroup}>
              {timeLimits.map((time) => (
                <button
                  key={time.value}
                  className={`${styles.optionBtn} ${config.timeLimit === time.value ? styles.optionBtnActive : ''}`}
                  onClick={() => setConfig({ ...config, timeLimit: time.value })}
                >
                  {time.label}
                </button>
              ))}
            </div>
          </div>

          {/* Options */}
          <div className={styles.formGroup}>
            <label className={styles.label}>Test Options</label>
            <div className={styles.optionsGrid}>
              <label className={styles.checkboxCard}>
                <input
                  type="checkbox"
                  checked={config.showFeedback}
                  onChange={(e) => setConfig({ ...config, showFeedback: e.target.checked })}
                />
                <div className={styles.checkboxContent}>
                  <div className={styles.checkboxLabel}>Show Feedback</div>
                  <div className={styles.checkboxDesc}>Display explanations after answers</div>
                </div>
              </label>

              <label className={styles.checkboxCard}>
                <input
                  type="checkbox"
                  checked={config.allowSkip}
                  onChange={(e) => setConfig({ ...config, allowSkip: e.target.checked })}
                />
                <div className={styles.checkboxContent}>
                  <div className={styles.checkboxLabel}>Allow Skip</div>
                  <div className={styles.checkboxDesc}>Skip questions without answering</div>
                </div>
              </label>

              <label className={styles.checkboxCard}>
                <input
                  type="checkbox"
                  checked={config.randomOrder}
                  onChange={(e) => setConfig({ ...config, randomOrder: e.target.checked })}
                />
                <div className={styles.checkboxContent}>
                  <div className={styles.checkboxLabel}>Randomize</div>
                  <div className={styles.checkboxDesc}>Shuffle question order</div>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={styles.setupFooter}>
          {onCancel && (
            <button className={styles.cancelBtn} onClick={onCancel}>
              Cancel
            </button>
          )}
          <button className={styles.startBtn} onClick={handleStart}>
            <span>Start Test</span>
            <ChevronRight size={18} />
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default TestSetup;
